"use client";

import React, { useEffect, useId, useRef, useState } from "react";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
  Button,
  type ButtonSize,
  type ButtonVariant
} from "../../atoms/button/button";
import { TabTray } from "../../atoms/tab-tray/tab-tray";
import { Text } from "../../atoms/text/text";
import { fireLinkClick } from "../../services/analytics";
import { ctaAriaLabel } from "../../services/cta-aria-label";
import { isSafeHref } from "../../services/safe-href";
import type {
  ComparisonCategory,
  ComparisonCompetitor,
  ComparisonFeature,
  ComparisonIcon,
  ComparisonTableProps
} from "./comparison-table.types";
import styles from "./comparison-table.module.css";
import { withSafeRender } from "../../atoms/safe-render";

const DIRECTV_LABEL = "DIRECTV";
const DEFAULT_COMPETITOR_GROUP_LABEL = "Choose a competitor to compare";
const DEFAULT_CATEGORY_GROUP_LABEL = "Comparison category";
const DEFAULT_CATEGORY_LINK_POSITION = "ComparisonTableCategorySelect";
const DEFAULT_COMPETITOR_LINK_POSITION = "ComparisonTableCompetitorSelect";
const DEFAULT_CTA_LINK_POSITION = "ComparisonTableCTA";

const COMPETITOR_SETTLE_MS = 150;

function CheckGlyph() {
  return (
    /* The frame's tick is a round-capped STROKED checkmark in green-400, sized to
       fill its box upright (~1.1 w:h, measured off 14059:24603). Sampling record in
       design-conflicts/comparison-table.mdx. */
    <svg aria-label="Yes" className={styles.glyph} fill="none" focusable="false" role="img" viewBox="0 0 24 24">
      <path
        d="M3 13 9.2 19.5 21 3.5"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.5"
      />
    </svg>
  );
}

function XGlyph() {
  return (
    <svg aria-label="No" className={styles.glyph} focusable="false" role="img" viewBox="0 0 24 24">
      <path
        d="M6.4 5 5 6.4 10.6 12 5 17.6 6.4 19 12 13.4 17.6 19 19 17.6 13.4 12 19 6.4 17.6 5 12 10.6z"
        fill="currentColor"
      />
    </svg>
  );
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function isSafeCompetitor(competitor: unknown): competitor is ComparisonCompetitor {
  return (
    typeof competitor === "object" &&
    competitor !== null &&
    "id" in competitor &&
    "name_label" in competitor &&
    "logo_url" in competitor &&
    typeof (competitor as any).id === "string" &&
    (competitor as any).id !== "" &&
    typeof (competitor as any).name_label === "string" &&
    (competitor as any).name_label !== "" &&
    typeof (competitor as any).logo_url === "string" &&
    (competitor as any).logo_url !== ""
  );
}

function isSafeCategory(category: unknown): category is ComparisonCategory {
  return (
    typeof category === "object" &&
    category !== null &&
    "id" in category &&
    "label" in category &&
    typeof (category as any).id === "string" &&
    (category as any).id !== "" &&
    typeof (category as any).label === "string" &&
    (category as any).label !== ""
  );
}

function isSafeFeature(feature: unknown): feature is ComparisonFeature {
  return (
    typeof feature === "object" &&
    feature !== null &&
    "label" in feature &&
    text((feature as any).label) !== ""
  );
}

interface ComparisonCellProps {
  value?: string;
  icon?: ComparisonIcon;
  note?: string;
}

function ComparisonCell({ value, icon, note }: ComparisonCellProps) {
  const glyph = icon === "check" ? <CheckGlyph /> : icon === "x" ? <XGlyph /> : null;
  const hasNote = text(note) !== "";
  const showValue = !glyph && text(value) !== "";

  if (!glyph && !showValue && !hasNote) {
    return null;
  }

  return (
    <div className={styles.cellInner}>
      {glyph && (
        <span className={icon === "check" ? styles.glyphCheck : styles.glyphX}>{glyph}</span>
      )}
      {showValue && (
        /* WAT-12254 #13: the frame's cell copy is Body 2 (16px) at regular
           weight, not Body 1 medium (20px/500) — which read as bold and
           oversized against the design. */
        <Text as="span" variant="body-2" className={styles.cellValue}>
          {value}
        </Text>
      )}
      {hasNote && (
        <Text as="span" variant="body-3" className={styles.cellNote}>
          {note}
        </Text>
      )}
    </div>
  );
}

function selectRenderableCompetitors(competitors: unknown): ComparisonCompetitor[] {
  const arr = Array.isArray(competitors) ? competitors : [];
  const seen = new Set<string>();
  const renderable: ComparisonCompetitor[] = [];

  for (const competitor of arr) {
    if (!isSafeCompetitor(competitor) || seen.has(competitor.id)) {
      continue;
    }
    seen.add(competitor.id);
    renderable.push(competitor);
  }

  return renderable;
}

function selectRenderableCategories(categories: unknown): ComparisonCategory[] {
  const arr = Array.isArray(categories) ? categories : [];
  const seen = new Set<string>();
  const renderable: ComparisonCategory[] = [];

  for (const category of arr) {
    if (!isSafeCategory(category) || seen.has(category.id)) {
      continue;
    }
    seen.add(category.id);
    renderable.push(category);
  }

  return renderable;
}

function selectRowsForView(
  features: unknown,
  categoryId: string,
  competitorId: string
): ComparisonFeature[] {
  const arr = Array.isArray(features) ? features : [];
  const seen = new Set<string>();
  const renderable: ComparisonFeature[] = [];

  for (const feature of arr) {
    if (!isSafeFeature(feature)) {
      continue;
    }
    const matchesCategory = !feature.category_id || feature.category_id === categoryId;
    const matchesCompetitor = !feature.competitor_id || feature.competitor_id === competitorId;
    if (!matchesCategory || !matchesCompetitor) {
      continue;
    }
    const key = `${feature.category_id || 'all'}-${feature.competitor_id || 'all'}-${feature.label}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    renderable.push(feature);
  }

  return renderable;
}

function isSafeColor(color: string): boolean {
  if (!color) {
    return false;
  }
  const trimmed = text(color);
  return /^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(trimmed);
}

function isSafeGradient(gradient: string): boolean {
  if (!gradient) {
    return false;
  }
  const trimmed = text(gradient);
  if (/url\(|expression\(|@import|javascript:/i.test(trimmed)) {
    return false;
  }
  return /^(linear|radial|conic|repeating-)?-?gradient\(/i.test(trimmed);
}

/* Narrow a CMS value to a published Button variant/size before it reaches
   Button (round-2 S3): the predicate on the category type vouches for these but
   isSafeCategory never checks them, so a mis-cased "Medium" would otherwise ride
   through and Button would resolve it to its OWN default (large), not ours.
   hasOwnProperty, not `in`, so a prototype key like "constructor" is refused. */
function isButtonVariant(value: unknown): value is ButtonVariant {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(BUTTON_VARIANTS, value);
}

function isButtonSize(value: unknown): value is ButtonSize {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(BUTTON_SIZES, value);
}

export interface ComparisonTableClientProps extends Omit<ComparisonTableProps, 'className'> {
  categoryStatusId: string;
}

function ComparisonTableClientImpl({
  competitors,
  categories,
  features,
  default_category_id: defaultCategoryId,
  default_competitor_id: defaultCompetitorId,
  // Deprecated table-level fallbacks (one release); a category's own field wins.
  primary_cta_label: primaryCtaLabel,
  primary_cta_url: primaryCtaUrl,
  primary_cta_variant: primaryCtaVariant,
  primary_cta_aria_label: primaryCtaAriaLabel,
  primary_cta_size: primaryCtaSize,
  category_link_position: categoryLinkPosition = DEFAULT_CATEGORY_LINK_POSITION,
  competitor_link_position: competitorLinkPosition = DEFAULT_COMPETITOR_LINK_POSITION,
  competitor_group_aria_label: competitorGroupAriaLabel = DEFAULT_COMPETITOR_GROUP_LABEL,
  category_group_aria_label: categoryGroupAriaLabel = DEFAULT_CATEGORY_GROUP_LABEL,
  directv_logo_url: directvLogoUrl,
  directv_logo_alt: directvLogoAlt = "directv",
  categoryStatusId
}: ComparisonTableClientProps) {
  const safeCompetitors = selectRenderableCompetitors(competitors);
  const safeCategories = selectRenderableCategories(categories);
  const groupId = useId();

  const startingCompetitorId = safeCompetitors.some(c => c.id === defaultCompetitorId) ?
    defaultCompetitorId :
    safeCompetitors[0]?.id;
  const startingCategoryId = safeCategories.some(c => c.id === defaultCategoryId) ?
    defaultCategoryId :
    safeCategories[0]?.id;

  const [chosenCompetitorId, setChosenCompetitorId] = useState(startingCompetitorId);
  const [chosenCategoryId, setChosenCategoryId] = useState(startingCategoryId);

  const reportedCompetitorIdRef = useRef(startingCompetitorId);
  const competitorSettleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (competitorSettleTimerRef.current !== null) {
      clearTimeout(competitorSettleTimerRef.current);
    }
  }, []);

  if (safeCompetitors.length === 0 || safeCategories.length === 0) {
    return null;
  }

  const activeCompetitorId = safeCompetitors.some(c => c.id === chosenCompetitorId) ?
    (chosenCompetitorId as string) :
    safeCompetitors[0].id;
  const activeCategoryId = safeCategories.some(c => c.id === chosenCategoryId) ?
    (chosenCategoryId as string) :
    safeCategories[0].id;
  const activeCompetitor = safeCompetitors.find(c => c.id === activeCompetitorId) ?? safeCompetitors[0];
  const activeCategory = safeCategories.find(c => c.id === activeCategoryId) ?? safeCategories[0];

  const rows = selectRowsForView(features, activeCategoryId, activeCompetitorId);

  /* The CTA comes from ONE source, not merged field by field (round-2 S1):
     combining a category's label with the table's url/aria yields a wrong
     destination and a Label-in-Name failure. A category "owns" the CTA once it
     sets a label or a url. The deprecated table-level CTA is the fallback ONLY
     while NO category has migrated (round-2 S2) — so once any category sets its
     own, an empty category shows no button rather than inheriting the table
     CTA. Removed with the table-level props after the Storyblok content
     migration; full rationale in design-conflicts/comparison-table.mdx. */
  const anyCategoryOwnsCta = safeCategories.some(
    c => text(c.primary_cta_label) !== "" || text(c.primary_cta_url) !== ""
  );
  const ctaSource: ComparisonCategory = anyCategoryOwnsCta ?
    activeCategory :
    {
      id: activeCategory.id,
      label: activeCategory.label,
      primary_cta_label: primaryCtaLabel,
      primary_cta_url: primaryCtaUrl,
      primary_cta_variant: primaryCtaVariant,
      primary_cta_size: primaryCtaSize,
      primary_cta_aria_label: primaryCtaAriaLabel
      // No table-level primary_cta_link_position — it defaults below.
    };

  const activeCtaLabel = text(ctaSource.primary_cta_label);
  const activeCtaUrl = text(ctaSource.primary_cta_url);
  const activeCtaAriaLabel = text(ctaSource.primary_cta_aria_label);
  const activeCtaVariant: ButtonVariant = isButtonVariant(ctaSource.primary_cta_variant) ?
    ctaSource.primary_cta_variant :
    "primary";
  const activeCtaSize: ButtonSize = isButtonSize(ctaSource.primary_cta_size) ?
    ctaSource.primary_cta_size :
    "medium";
  const hasCta = activeCtaLabel !== "" && isSafeHref(activeCtaUrl);
  const lastFeatureRow = !hasCta && rows.length > 0;

  const renderCtaButton = () => {
    if (!hasCta) {
      return null;
    }
    return (
      /* ATTRIBUTES, NOT A PUSH: GTM's Link Click trigger is gated on
         data-linkposition/data-linkdestination, so the CTA (guarded to an <a>
         by isSafeHref above) only reports once `linkAttributes` writes those
         for GTM to read. `linkName` would replace that report with Button's
         own push rather than add to it (WAT-12254 #1) — one report either
         way, but named from a CMS field instead of the accessible name, so
         `linkAttributes` is the shape the house rule asks for and pushes
         nothing itself. */
      <Button
        ariaLabel={ctaAriaLabel(activeCtaAriaLabel)}
        href={activeCtaUrl}
        linkAttributes
        linkPosition={text(ctaSource.primary_cta_link_position) || DEFAULT_CTA_LINK_POSITION}
        size={activeCtaSize}
        variant={activeCtaVariant}
      >
        {activeCtaLabel}
      </Button>
    );
  };

  function handleCompetitorChange(competitor: ComparisonCompetitor) {
    setChosenCompetitorId(competitor.id);

    if (competitorSettleTimerRef.current !== null) {
      clearTimeout(competitorSettleTimerRef.current);
    }
    competitorSettleTimerRef.current = setTimeout(() => {
      competitorSettleTimerRef.current = null;
      if (reportedCompetitorIdRef.current !== competitor.id) {
        reportedCompetitorIdRef.current = competitor.id;
        fireLinkClick({
          linkName: competitor.name_label,
          linkPosition: competitorLinkPosition
        });
      }
    }, COMPETITOR_SETTLE_MS);
  }

  return (
    <>
      <div id={categoryStatusId} aria-live="polite" aria-atomic="true" className="dds-sr-only">
        Showing {activeCategory?.label} features for {activeCompetitor.name_label}
      </div>

      <div className={styles.content}>
          <fieldset className={styles.competitorGroup}>
          <legend className="dds-sr-only">{text(competitorGroupAriaLabel) || DEFAULT_COMPETITOR_GROUP_LABEL}</legend>
          <div className={styles.competitorList}>
            {safeCompetitors.map(competitor => {
              const inputId = `${groupId}-competitor-${competitor.id}`;
              return (
                <span
                  key={competitor.id}
                  className={styles.competitorSlot}
                  style={
                    {
                      "--glow-color": isSafeColor(competitor.glow_color) ? competitor.glow_color : undefined,
                      "--bg-gradient": isSafeGradient(competitor.background_gradient) ? competitor.background_gradient : undefined
                    } as React.CSSProperties
                  }
                >
                  <input
                    checked={activeCompetitorId === competitor.id}
                    className={styles.competitorInput}
                    id={inputId}
                    name={`${groupId}-competitor`}
                    onChange={() => handleCompetitorChange(competitor)}
                    type="radio"
                    value={competitor.id}
                  />
                  {text(competitor.glow_color) !== "" && (
                    <span aria-hidden className={styles.glow}>
                      <span className={styles.glowShape} />
                    </span>
                  )}
                  <label className={styles.competitorTile} htmlFor={inputId}>
                    <img
                      aria-hidden
                      className={styles.competitorLogo}
                      loading="lazy"
                      src={text(competitor.logo_url)}
                      alt={text(competitor.logo_alt) || competitor.name_label}
                    />
                    <span className="dds-sr-only">{competitor.name_label}</span>
                  </label>
                </span>
              );
            })}
          </div>
        </fieldset>

        <div className={styles.tableContainer} data-dds-surface="glass-surface">
          <TabTray
            ariaLabel={text(categoryGroupAriaLabel) || DEFAULT_CATEGORY_GROUP_LABEL}
            className={styles.categoryTray}
            items={safeCategories.map(category => ({
              value: category.id,
              label: category.label
            }))}
            /* Active category's own position, else the deprecated table-level
               fallback; see design-conflicts for why the settle-time read
               reports the newly selected tab. */
            linkPosition={text(activeCategory.category_link_position) || categoryLinkPosition}
            onChange={setChosenCategoryId}
            value={activeCategoryId}
          />

          <div className={styles.tableFrame}>
            <table className={styles.table}>
              <caption className="dds-sr-only">{`DIRECTV compared with ${activeCompetitor.name_label}`}</caption>
              <thead>
                <tr>
                  <th className={styles.cornerCell} scope="col" />
                  <th
                    className={
                      rows.length === 0 && !hasCta ?
                        /* With no body rows and no CTA, the header cell is the
                           only DIRECTV cell, so it must round its own bottom to
                           match the always-rounded rim overlay (nit). */
                        `${styles.directvHeaderCell} ${styles.directvCellLast}` :
                        styles.directvHeaderCell
                    }
                    scope="col"
                  >
                    {text(directvLogoUrl) !== "" ? (
                      <img
                        alt={text(directvLogoAlt)}
                        className={styles.directvWordmark}
                        loading="lazy"
                        src={text(directvLogoUrl)}
                      />
                    ) : (
                      <Text as="span" className={styles.directvWordmark} variant="headline-3">
                        {DIRECTV_LABEL}
                      </Text>
                    )}
                  </th>
                  <th className={styles.competitorHeaderCell} scope="col">
                    <img
                      alt={text(activeCompetitor.logo_alt) || activeCompetitor.name_label}
                      className={styles.competitorHeaderLogo}
                      loading="lazy"
                      src={text(activeCompetitor.logo_url)}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((feature, index) => {
                  const isLastRow = lastFeatureRow && index === rows.length - 1;
                  return (
                    <tr className={styles.row} key={`${feature.category_id || 'all'}-${feature.competitor_id || 'all'}-${feature.label}`}>
                      <th className={styles.rowHeaderCell} scope="row">
                        {/* WAT-12254 #13: Body 2 regular, matching the cell copy
                            above — the row labels were Body 1 medium (bold/large). */}
                        <Text as="span" className={styles.rowLabel} variant="body-2">
                          {feature.label}
                        </Text>
                      </th>
                      <td
                        className={
                          isLastRow ?
                            `${styles.directvCell} ${styles.directvCellLast}` :
                            styles.directvCell
                        }
                      >
                        <ComparisonCell
                          icon={feature.directv_icon}
                          note={feature.directv_note_content}
                          value={feature.directv_content}
                        />
                      </td>
                      <td className={styles.competitorCell}>
                        <ComparisonCell
                          icon={feature.competitor_icon}
                          note={feature.competitor_note_content}
                          value={feature.competitor_content}
                        />
                      </td>
                    </tr>
                  );
                })}
                {hasCta && (
                  <tr className={`${styles.row} ${styles.ctaRow}`}>
                    <td className={styles.rowHeaderCell} />
                    <td className={`${styles.directvCell} ${styles.directvCellLast} ${styles.ctaCell}`}>
                      {renderCtaButton()}
                    </td>
                    <td className={styles.competitorCell} />
                  </tr>
                )}
              </tbody>
            </table>
            {/* WAT-12254 #4A/#10: the DIRECTV column's cyan→blue gradient rim and
                glow, drawn as one overlay over the column (styles.directvColumnRim)
                instead of per-cell solid borders — a continuous gradient a 2px
                cell border cannot be. Decorative; the <table>'s own row/column
                headers carry the semantics. */}
            <span aria-hidden className={styles.directvColumnRim} />
          </div>
          {hasCta && (
            <div className={styles.ctaBlock}>
              {renderCtaButton()}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
export const ComparisonTableClient = withSafeRender(ComparisonTableClientImpl, "ComparisonTableClient");
